import { useRef, useMemo, useCallback } from 'react';
import { makeStyles } from 'tss-react/mui';
import Box, { BoxProps } from '@mui/material/Box';

const FONT_SIZE = 13;
const LINE_HEIGHT = 1.6;
const PADDING_V = 12; // 1.5 * 8 spacing unit

const useStyles = makeStyles()((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    '&:focus-within': {
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
    },
  },
  body: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    overflow: 'hidden',
  },
  gutter: {
    flexShrink: 0,
    overflow: 'hidden',
    userSelect: 'none',
    padding: `${PADDING_V}px 8px ${PADDING_V}px 4px`,
    fontFamily: '"Fira Code", "Consolas", monospace',
    fontSize: FONT_SIZE,
    lineHeight: LINE_HEIGHT,
    color: theme.palette.text.disabled,
    textAlign: 'right',
    borderRight: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.mode === 'dark'
      ? 'rgba(255,255,255,0.02)'
      : 'rgba(0,0,0,0.02)',
    whiteSpace: 'pre',
    boxSizing: 'border-box',
  },
  textarea: {
    flex: 1,
    resize: 'none',
    padding: `${PADDING_V}px 12px`,
    fontFamily: '"Fira Code", "Consolas", monospace',
    fontSize: FONT_SIZE,
    lineHeight: LINE_HEIGHT,
    border: 'none',
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    outline: 'none',
    boxSizing: 'border-box',
    overflowY: 'auto',
    caretColor: theme.palette.primary.main,
    '&::placeholder': {
      color: theme.palette.text.disabled,
      opacity: 1,
    },
    '&:read-only': {
      backgroundColor: theme.palette.mode === 'dark'
        ? 'rgba(255,255,255,0.02)'
        : 'rgba(0,0,0,0.02)',
      cursor: 'default',
    },
  },
}));

interface CodeEditorProps {
  value: string;
  onChange?: (value: string) => void;
  readOnly?: boolean;
  placeholder?: string;
  /** Number of visible rows used to compute min-height. Default 20. */
  rows?: number;
  /** Extra sx styles applied to the root Box (e.g. flex: 1 for full-height layouts). */
  sx?: BoxProps['sx'];
}

export function CodeEditor({ value, onChange, readOnly, placeholder, rows = 20, sx }: CodeEditorProps) {
  const { classes } = useStyles();
  const gutterRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Compute gutter width based on number of digits in line count
  const lineCount = useMemo(() => (value || '').split('\n').length, [value]);
  const digits = String(lineCount).length;
  const gutterWidth = Math.max(28, digits * 9 + 20);

  const lineNumbers = useMemo(
    () => Array.from({ length: lineCount }, (_, i) => i + 1).join('\n'),
    [lineCount],
  );

  const syncScroll = useCallback(() => {
    if (textareaRef.current && gutterRef.current) {
      gutterRef.current.scrollTop = textareaRef.current.scrollTop;
    }
  }, []);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange?.(e.target.value);
    },
    [onChange],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key !== 'Tab') return;
      e.preventDefault();

      // execCommand preserves the native browser undo stack (Ctrl+Z/Ctrl+Y work).
      // It is deprecated but still universally supported for this use-case.
      const inserted = document.execCommand('insertText', false, '  ');
      if (!inserted) {
        // Fallback: direct DOM manipulation (undo of this specific step won't work,
        // but regular typed text undo is unaffected).
        const el = e.currentTarget;
        const start = el.selectionStart;
        const end = el.selectionEnd;
        const next = `${el.value.substring(0, start)}  ${el.value.substring(end)}`;
        onChange?.(next);
        requestAnimationFrame(() => {
          el.selectionStart = start + 2;
          el.selectionEnd = start + 2;
        });
      }
    },
    [onChange],
  );

  const minHeight = rows * FONT_SIZE * LINE_HEIGHT + PADDING_V * 2;

  return (
    <Box className={classes.root} sx={sx}>
      <Box className={classes.body} style={{ minHeight }}>
        <div
          ref={gutterRef}
          className={classes.gutter}
          style={{ width: gutterWidth }}
          aria-hidden
        >
          {lineNumbers}
        </div>
        <textarea
          ref={textareaRef}
          className={classes.textarea}
          value={value}
          onChange={handleChange}
          onKeyDown={readOnly ? undefined : handleKeyDown}
          onScroll={syncScroll}
          readOnly={readOnly}
          placeholder={placeholder}
          spellCheck={false}
        />
      </Box>
    </Box>
  );
}
